// ============================================================================
// CONFIG LIST EDITOR
// ============================================================================
// Ordered multi-value editor for definitions with is_multi_value = TRUE.
// Persistence lives in config.tbl_config_list_item (one row per element,
// keyed by item_order); this editor works with a plain string[] and the
// parent hook (useConfigSettings) handles the row-level CRUD on save.
//
// Concrete FBP use: REPORT.DEBTORS.BUCKETS = [90, 180, 270, 365].
// ============================================================================

import React, { useCallback } from 'react';

interface ConfigListEditorProps {
  items: string[];
  onChange: (items: string[]) => void;
  /** Placeholder hint (e.g. "days", "rows"). */
  unitLabel?: string;
  isReadOnly?: boolean;
}

const ConfigListEditor: React.FC<ConfigListEditorProps> = ({
  items,
  onChange,
  unitLabel,
  isReadOnly = false,
}) => {
  const handleItemChange = useCallback(
    (index: number, newValue: string) => {
      const updated = items.slice();
      updated[index] = newValue;
      onChange(updated);
    },
    [items, onChange],
  );

  const handleAdd = useCallback(() => {
    onChange([...items, '']);
  }, [items, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      onChange(items.filter((_, i) => i !== index));
    },
    [items, onChange],
  );

  const handleMove = useCallback(
    (index: number, direction: -1 | 1) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= items.length) return;
      const updated = items.slice();
      [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
      onChange(updated);
    },
    [items, onChange],
  );

  return (
    <div className="config-list-editor">
      {items.map((item, index) => (
        <div key={index} className="config-list-item">
          <input
            type="text"
            value={item}
            onChange={(e) => handleItemChange(index, e.target.value)}
            disabled={isReadOnly}
            placeholder={unitLabel ? `Value (${unitLabel})` : 'Enter value'}
          />
          {!isReadOnly && (
            <>
              <button
                type="button"
                className="config-list-btn"
                onClick={() => handleMove(index, -1)}
                disabled={index === 0}
                title="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                className="config-list-btn"
                onClick={() => handleMove(index, 1)}
                disabled={index === items.length - 1}
                title="Move down"
              >
                ↓
              </button>
              <button
                type="button"
                className="config-list-btn config-list-btn-danger"
                onClick={() => handleRemove(index)}
                title="Remove"
              >
                ✕
              </button>
            </>
          )}
        </div>
      ))}
      {!isReadOnly && (
        <button type="button" className="config-list-add-btn" onClick={handleAdd}>
          + Add item
        </button>
      )}
    </div>
  );
};

export default ConfigListEditor;
